/* Scratch card email popup — config comes from data attributes on .scp-root */
(function () {
  'use strict';

  /* Measured on the live card: one full-width swipe clears ~0.17-0.22 of the
     surface, so the old 0.45 threshold demanded four deliberate swipes before
     the Claim button was even made visible — visitors who tapped or scratched
     once saw no way forward and dropped out. ~0.22 reveals after roughly one
     full swipe, which still reads as "I scratched it". */
  var REVEAL_THRESHOLD = 0.22;
  /* If someone scratches a bit then stops, reveal for them rather than
     leaving them stuck below the threshold. Requires real scratching, so the
     reward is still never given away to someone who hasn't touched the card. */
  var IDLE_REVEAL_THRESHOLD = 0.1;
  var IDLE_REVEAL_MS = 900;
  var BRUSH_RADIUS = 26;

  /* Lightweight funnel instrumentation. Nothing is sent anywhere by itself —
     these are plain CustomEvents so GA4/Meta/etc. can be wired to them
     without touching this file again. Without these there is no way to tell
     whether a low submit rate comes from the popup not being seen, the
     scratch step, or the email step. */
  function track(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent('scp:' + name, { detail: detail || {} }));
    } catch (e) {
      /* CustomEvent unsupported — tracking is optional, never break the flow */
    }
  }

  function ScratchCardPopup(root) {
    this.root = root;
    this.cfg = root.dataset;
    this.modal = root.querySelector('.scp-modal');
    this.canvas = root.querySelector('.scp-canvas');
    this.card = root.querySelector('.scp-card');
    this.form = root.querySelector('.scp-form');
    this.emailInput = root.querySelector('[name="contact\\[email\\]"]');
    this.nameInput = root.querySelector('[name="contact\\[first_name\\]"]');
    this.errorEl = root.querySelector('.scp-error');
    this.submitBtn = root.querySelector('.scp-submit');
    this.claimBtn = root.querySelector('.scp-claim');
    this.teaser = document.querySelector('.scp-teaser');
    this.steps = {
      scratch: root.querySelector('[data-step="scratch"]'),
      email: root.querySelector('[data-step="email"]'),
      success: root.querySelector('[data-step="success"]')
    };
    this.storageKey = 'scp_state_' + (this.cfg.sectionId || 'default');
    this.revealed = false;
    this.drawing = false;
    this.lastFocus = null;
    this.erasedArea = 0;

    this.loadTexture();
    this.bind();
    this.schedule();
  }

  /* Optional foil photo. crossOrigin is required: without it the Shopify CDN
     image taints the canvas and getImageData() throws, killing progress
     tracking. Shopify's CDN sends permissive CORS headers, so this is safe. */
  ScratchCardPopup.prototype.loadTexture = function () {
    var url = this.cfg.scratchTexture;
    if (!url) return;
    var self = this;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      self.textureImg = img;
      // Repaint only if the visitor hasn't started scratching yet
      if (!self.revealed && self.erasedArea === 0 && self.ctx && self.cssSize) {
        self.ctx.globalCompositeOperation = 'source-over';
        self.paintCover(self.ctx, self.cssSize.w, self.cssSize.h);
        self.ctx.globalCompositeOperation = 'destination-out';
      }
    };
    img.onerror = function () {
      /* fall back to the procedural foil */
    };
    img.src = url;
  };

  ScratchCardPopup.prototype.storage = function (value) {
    try {
      if (value === undefined) return JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      localStorage.setItem(this.storageKey, JSON.stringify(value));
    } catch (e) {
      /* private mode / blocked storage — treat as first visit */
    }
    return {};
  };

  /* After a real form submit + page reload, Shopify re-renders this section
     with form.posted_successfully?/form.errors populated (surfaced as JSON
     by the Liquid template) — pick that up and reopen straight to the
     relevant step instead of running the normal delay/dismissal logic. */
  ScratchCardPopup.prototype.checkForRedirectState = function () {
    var stateEl = this.root.querySelector('[data-scp-form-state]');
    if (!stateEl) return false;
    var state;
    try {
      state = JSON.parse(stateEl.textContent);
    } catch (e) {
      return false;
    }
    if (state.postedSuccessfully) {
      var stored = this.storage();
      stored.subscribed = true;
      this.storage(stored);
      this.open();
      this.step('success');
      if (this.teaser) this.teaser.hidden = true;
      track('signup_confirmed');
      return true;
    }
    if (state.hasErrors) {
      this.open();
      this.step('email');
      return true;
    }
    return false;
  };

  ScratchCardPopup.prototype.schedule = function () {
    if (this.checkForRedirectState()) return;

    var mode = this.cfg.mode;
    if (mode === 'disabled') return;

    // Always visible in the theme editor and in test mode so it can be styled.
    var isEditor = window.Shopify && window.Shopify.designMode;
    if (mode === 'testing' || isEditor) {
      this.open();
      return;
    }

    var state = this.storage();
    if (state.subscribed) return;
    if (state.dismissedAt) {
      var days = parseInt(this.cfg.frequencyDays, 10) || 30;
      if (Date.now() - state.dismissedAt < days * 86400000) {
        this.showTeaser();
        return;
      }
    }

    var self = this;
    var delay = (parseInt(this.cfg.delaySeconds, 10) || 5) * 1000;
    setTimeout(function () {
      self.open();
    }, delay);
  };

  ScratchCardPopup.prototype.open = function () {
    this.lastFocus = document.activeElement;
    this.root.hidden = false;
    if (this.teaser) this.teaser.hidden = true;
    document.body.style.overflow = 'hidden';
    this.initCanvas();
    // Focus the dialog itself, not the card — programmatic focus on the card
    // renders a focus ring that reads as an unwanted border.
    if (this.modal) this.modal.focus({ preventScroll: true });

    track('shown');
  };

  ScratchCardPopup.prototype.close = function (opts) {
    this.root.hidden = true;
    document.body.style.overflow = '';
    clearTimeout(this.idleRevealTimer);
    track('dismissed', {
      revealed: !!this.revealed,
      scratched: !!this.scratchStarted,
      onStep: this.currentStep || 'scratch'
    });
    if (!(opts && opts.done)) {
      var state = this.storage();
      state.dismissedAt = Date.now();
      this.storage(state);
      this.showTeaser();
    }
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  ScratchCardPopup.prototype.showTeaser = function () {
    if (this.teaser && this.cfg.showTeaser === 'true') this.teaser.hidden = false;
  };

  ScratchCardPopup.prototype.step = function (name) {
    this.currentStep = name;
    var steps = this.steps;
    Object.keys(steps).forEach(function (key) {
      if (steps[key]) steps[key].hidden = key !== name;
    });
    var focusEl = steps[name] && steps[name].querySelector('input, button');
    if (focusEl) focusEl.focus({ preventScroll: true });
  };

  /* ---------- Scratch canvas ---------- */

  ScratchCardPopup.prototype.initCanvas = function () {
    if (!this.canvas || this.canvasReady) return;
    var canvas = this.canvas;
    var rect = this.card.getBoundingClientRect();
    if (!rect.width) return; // not laid out yet

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    this.paintCover(ctx, rect.width, rect.height);

    ctx.globalCompositeOperation = 'destination-out';
    this.ctx = ctx;
    this.cssSize = { w: rect.width, h: rect.height };
    this.canvasReady = true;
  };

  /* Deterministic pseudo-random so the foil looks organic but never jitters
     between repaints (resize, re-open). */
  function rand(seed) {
    var s = seed;
    return function () {
      s = (s * 1664525 + 1013904223) % 4294967296;
      return s / 4294967296;
    };
  }

  /* Fine grain, generated once and reused as a pattern — this is what stops
     the foil reading as a flat CSS gradient. */
  ScratchCardPopup.prototype.grainPattern = function (ctx) {
    if (this._grain !== undefined) return this._grain;
    try {
      var size = 64;
      var off = document.createElement('canvas');
      off.width = off.height = size;
      var octx = off.getContext('2d');
      var img = octx.createImageData(size, size);
      var rnd = rand(20260906);
      for (var i = 0; i < img.data.length; i += 4) {
        var v = 120 + Math.floor(rnd() * 135);
        img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
      octx.putImageData(img, 0, 0);
      this._grain = ctx.createPattern(off, 'repeat');
    } catch (e) {
      this._grain = null;
    }
    return this._grain;
  };

  ScratchCardPopup.prototype.paintCover = function (ctx, w, h) {
    var base = this.cfg.scratchColor || '#c9a227';
    var shine = this.cfg.scratchColor2 || '#f7e08a';

    // A merchant-supplied foil photo beats anything procedural — use it if loaded
    if (this.textureImg && this.textureImg.complete && this.textureImg.naturalWidth) {
      var img = this.textureImg;
      var scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
      var dw = img.naturalWidth * scale;
      var dh = img.naturalHeight * scale;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    } else {
      // Warm metal base
      var grad = ctx.createLinearGradient(0, h, w, 0);
      grad.addColorStop(0, base);
      grad.addColorStop(0.28, shine);
      grad.addColorStop(0.52, base);
      grad.addColorStop(0.78, shine);
      grad.addColorStop(1, base);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Soft diagonal sheen — feathered stops, no hard banding
      var sheen = ctx.createLinearGradient(0, h, w, 0);
      sheen.addColorStop(0, 'rgba(255,255,255,0)');
      sheen.addColorStop(0.3, 'rgba(255,255,255,0.26)');
      sheen.addColorStop(0.46, 'rgba(255,255,255,0.02)');
      sheen.addColorStop(0.64, 'rgba(255,255,255,0.3)');
      sheen.addColorStop(0.82, 'rgba(255,255,255,0.04)');
      sheen.addColorStop(1, 'rgba(255,255,255,0.14)');
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, w, h);

      // Crinkle: soft irregular light/dark pools, the thing that separates
      // real foil from a flat gradient
      var rndC = rand(4242);
      ctx.save();
      for (var c = 0; c < 14; c++) {
        var cx = rndC() * w;
        var cy = rndC() * h;
        var r = Math.min(w, h) * (0.12 + rndC() * 0.3);
        var light = rndC() > 0.45;
        var blob = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        blob.addColorStop(
          0,
          light ? 'rgba(255,248,214,' + (0.1 + rndC() * 0.16).toFixed(3) + ')' : 'rgba(120,88,26,' + (0.06 + rndC() * 0.12).toFixed(3) + ')'
        );
        blob.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = blob;
        ctx.fillRect(0, 0, w, h);
      }
      ctx.restore();

      // Foil grain
      var pattern = this.grainPattern(ctx);
      if (pattern) {
        ctx.save();
        ctx.globalCompositeOperation = 'overlay';
        ctx.globalAlpha = 0.16;
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, w, h);
        ctx.restore();
      }

      // Edge vignette for depth
      var vig = ctx.createRadialGradient(w * 0.5, h * 0.45, Math.min(w, h) * 0.2, w * 0.5, h * 0.5, w * 0.75);
      vig.addColorStop(0, 'rgba(0,0,0,0)');
      vig.addColorStop(1, 'rgba(90,60,10,0.22)');
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, w, h);
    }

    // Organic brush-stroke scuffs behind the label (tapered, jittered, tonal)
    var rnd2 = rand(918273);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.translate(w * 0.5, h * 0.5);
    for (var s = 0; s < 9; s++) {
      // Each stroke gets its own angle/length so the cluster reads as a
      // hand scribble rather than a row of parallel bars
      ctx.save();
      ctx.translate((s - 4) * w * 0.055 + (rnd2() - 0.5) * w * 0.03, (rnd2() - 0.5) * h * 0.1);
      ctx.rotate(-0.3 + (rnd2() - 0.5) * 0.55);
      var len = h * (0.16 + rnd2() * 0.24);
      ctx.strokeStyle = 'rgba(126,90,22,' + (0.16 + rnd2() * 0.16).toFixed(3) + ')';
      ctx.lineWidth = w * (0.028 + rnd2() * 0.032);
      ctx.beginPath();
      ctx.moveTo(0, -len);
      ctx.quadraticCurveTo((rnd2() - 0.5) * w * 0.05, 0, (rnd2() - 0.5) * w * 0.04, len);
      ctx.stroke();
      ctx.restore();
    }
    ctx.restore();

    var label = this.cfg.scratchLabel;
    if (label) {
      var text = label.toUpperCase();
      var font = this.cfg.scratchFont || 'Helvetica, Arial, sans-serif';
      var size = Math.max(16, Math.round(w * 0.105));
      var maxWidth = w * 0.8;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      try {
        ctx.letterSpacing = Math.round(size * 0.06) + 'px';
      } catch (e) {
        /* letterSpacing unsupported — harmless */
      }
      // Shrink until the label fits the card instead of bleeding off the edges
      ctx.font = '700 ' + size + 'px ' + font;
      while (size > 11 && ctx.measureText(text).width > maxWidth) {
        size -= 1;
        ctx.font = '700 ' + size + 'px ' + font;
      }
      ctx.fillStyle = 'rgba(255,255,255,0.84)';
      ctx.fillText(text, w / 2, h / 2);
    }
  };

  ScratchCardPopup.prototype.pointerPos = function (e) {
    var rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  ScratchCardPopup.prototype.scratchAt = function (pos, prev) {
    var ctx = this.ctx;
    if (!ctx) return;
    ctx.beginPath();
    if (prev) {
      ctx.lineWidth = BRUSH_RADIUS * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      var dx = pos.x - prev.x;
      var dy = pos.y - prev.y;
      this.erasedArea += Math.sqrt(dx * dx + dy * dy) * BRUSH_RADIUS * 2;
    } else {
      this.erasedArea += Math.PI * BRUSH_RADIUS * BRUSH_RADIUS;
    }
    ctx.arc(pos.x, pos.y, BRUSH_RADIUS, 0, Math.PI * 2);
    ctx.fill();
  };

  ScratchCardPopup.prototype.scratchedFraction = function () {
    var w = this.canvas.width;
    var h = this.canvas.height;
    try {
      // Sample a coarse grid rather than every pixel — cheap per pointer batch.
      var data = this.ctx.getImageData(0, 0, w, h).data;
      var clear = 0;
      var total = 0;
      for (var i = 3; i < data.length; i += 4 * 64) {
        total++;
        if (data[i] < 40) clear++;
      }
      return total ? clear / total : 0;
    } catch (e) {
      // Canvas tainted (e.g. a texture served without CORS) — estimate from
      // brush travel instead, discounted for overlapping strokes.
      var area = this.cssSize ? this.cssSize.w * this.cssSize.h : 0;
      return area ? Math.min(1, (this.erasedArea * 0.55) / area) : 0;
    }
  };

  /* The Claim button only ever appears here — i.e. only once the card has
     actually been scratched past the threshold (or auto-revealed after a
     partial scratch). It is deliberately NOT surfaced on first touch or on a
     timer: doing so gave the reward away before the visitor engaged with the
     card, which defeats the point of the scratch-off. */
  ScratchCardPopup.prototype.reveal = function () {
    if (this.revealed) return;
    this.revealed = true;
    if (this.canvas) this.canvas.classList.add('scp-revealed');
    if (this.claimBtn) {
      this.claimBtn.disabled = false;
      this.claimBtn.classList.add('scp-visible');
    }
    if (this.card) this.card.setAttribute('aria-label', this.cfg.revealedLabel || 'Reward revealed');
    track('revealed');
  };

  /* ---------- Events ---------- */

  ScratchCardPopup.prototype.bind = function () {
    var self = this;

    // Scratch interaction
    if (this.canvas) {
      var prev = null;
      var moves = 0;

      this.canvas.addEventListener('pointerdown', function (e) {
        self.initCanvas();
        self.drawing = true;
        prev = null;
        clearTimeout(self.idleRevealTimer);
        self.canvas.setPointerCapture(e.pointerId);
        if (!self.scratchStarted) {
          self.scratchStarted = true;
          track('scratch_start');
        }
        var pos = self.pointerPos(e);
        self.scratchAt(pos, null);
        prev = pos;
      });

      this.canvas.addEventListener('pointermove', function (e) {
        if (!self.drawing || self.revealed) return;
        var pos = self.pointerPos(e);
        self.scratchAt(pos, prev);
        prev = pos;
        if (++moves % 8 === 0 && self.scratchedFraction() >= REVEAL_THRESHOLD) self.reveal();
      });

      var stop = function (e) {
        if (!self.drawing) return;
        self.drawing = false;
        prev = null;
        if (e && e.pointerId != null && self.canvas.hasPointerCapture(e.pointerId)) {
          self.canvas.releasePointerCapture(e.pointerId);
        }
        if (self.revealed) return;
        var fraction = self.scratchedFraction();
        if (fraction >= REVEAL_THRESHOLD) {
          self.reveal();
          return;
        }
        // Scratched something then stopped — finish it for them shortly after,
        // rather than stranding them part-way with the reward still covered.
        if (fraction >= IDLE_REVEAL_THRESHOLD) {
          clearTimeout(self.idleRevealTimer);
          self.idleRevealTimer = setTimeout(function () {
            if (!self.revealed && !self.drawing) self.reveal();
          }, IDLE_REVEAL_MS);
        }
      };
      this.canvas.addEventListener('pointerup', stop);
      this.canvas.addEventListener('pointercancel', stop);
      this.canvas.addEventListener('pointerleave', stop);
    }

    // Keyboard: reveal without scratching (matches the reference popup's a11y behaviour)
    if (this.card) {
      this.card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          self.reveal();
        }
      });
    }

    if (this.claimBtn) {
      this.claimBtn.addEventListener('click', function () {
        self.reveal();
        self.step('email');
        track('email_step');
      });
    }

    // Close controls
    this.root.querySelectorAll('.scp-close').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.close();
      });
    });
    this.root.addEventListener('click', function (e) {
      if (e.target.classList.contains('scp-backdrop')) self.close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.root.hidden) self.close();
      if (e.key === 'Tab' && !self.root.hidden) self.trapFocus(e);
    });
    if (this.teaser) {
      this.teaser.addEventListener('click', function () {
        self.open();
      });
    }

    // Form submit — a real submission (page reload via Shopify's own
    // customer form), only intercepted to block obviously-invalid email.
    // See checkForRedirectState() for what happens after the reload.
    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        if (!self.validateEmail()) {
          e.preventDefault();
          return;
        }
        self.submitBtn.disabled = true;
        self.submitBtn.textContent = self.cfg.submittingLabel || 'Submitting…';
        track('submitted');
      });
    }

    // Copy discount code
    var codeEl = this.root.querySelector('.scp-code');
    if (codeEl) {
      codeEl.addEventListener('click', function () {
        var code = codeEl.dataset.code || '';
        var done = codeEl.querySelector('.scp-copied');
        var finish = function () {
          if (!done) return;
          done.classList.add('scp-show');
          setTimeout(function () {
            done.classList.remove('scp-show');
          }, 1600);
        };
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).then(finish, function () {});
        }
      });
    }

    window.addEventListener('resize', function () {
      if (self.revealed || self.root.hidden) return;
      self.canvasReady = false;
      self.initCanvas();
    });
  };

  ScratchCardPopup.prototype.trapFocus = function (e) {
    var focusables = this.modal.querySelectorAll(
      'button:not([disabled]), input, a[href], [tabindex]:not([tabindex="-1"])'
    );
    var visible = Array.prototype.filter.call(focusables, function (el) {
      return el.offsetParent !== null;
    });
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  ScratchCardPopup.prototype.validateEmail = function () {
    var email = (this.emailInput.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      this.setError(this.cfg.errorEmail || 'Please enter a valid email address.');
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
      return false;
    }
    this.emailInput.removeAttribute('aria-invalid');
    this.setError('');
    return true;
  };

  ScratchCardPopup.prototype.setError = function (msg) {
    if (this.errorEl) this.errorEl.textContent = msg;
  };

  function init() {
    document.querySelectorAll('.scp-root').forEach(function (root) {
      if (!root.dataset.initialised) {
        root.dataset.initialised = 'true';
        new ScratchCardPopup(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
